// ClientDocuments.js — the CLIENT-level document view, and (given consultantId) the CONSULTANT-level one.
//
// Consultant mode (Consultants → a firm): documents filed against the firm — file_exchange.consultant_id, which the
// database allows only with no client, property or parcel (migration 2026-10-08_consultant_doc_level). Staff only:
// no portal shows these.
//
// Why this exists: a document filed to a client with no property (a contract, an onboarding checklist)
// was previously invisible everywhere. Every documents surface scopes by property — DocumentsBrowser
// (`.in('property_id', propIds)`), the property/parcel bubble, and the File Exchange tables (which also
// filter by direction). So the write path worked and nothing could read it back.
//
// Scope: FILED (filed_at not null) + client_id = X + property_id IS NULL. Untriaged intake rows are NOT
// shown — they're the Document Intake tray, and most of them will file to a property, not the client.
// Deliberately NO folder allow-list (a hardcoded list of displayable folders is the bug this view exists
// to fix) and NO direction filter (a doc filed off a task is 'ntrg_internal').
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import { useDocVocab } from '../utils/docVocab';
import { useSkipNotes, skipTooltip } from '../utils/fileWithoutScanning';

const fmtDate = ts => {
  if (!ts) return null;
  const d = new Date(ts);   // timestamptz → local; no date-only parsing here, so no UTC-shift trap
  return isNaN(d) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// No-year first, then year descending — same order as the documents bubble, so a document that isn't
// year-specific reads at the top rather than after the newest year.
const byYear = (a, b) => {
  const ay = a.year_subfolder, by = b.year_subfolder;
  if (!ay && !by) return String(b.filed_at || '').localeCompare(String(a.filed_at || ''));
  if (!ay) return -1;
  if (!by) return 1;
  return String(by).localeCompare(String(ay));
};

export default function ClientDocuments({ clientId, clientName, consultantId, consultantName, c, s }) {
  const forConsultant = !!consultantId;
  const [files, setFiles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [openFolder, setOpenFolder] = useState(null);
  const { vocab } = useDocVocab();   // folder/type display names come from the table
  const skipNotes = useSkipNotes(files);   // who skipped extraction, when, and why — the chip's tooltip
  const load = useCallback(async () => {
    if (!clientId && !consultantId) { setFiles([]); setLoading(false); return; }
    setLoading(true); setErr(null);
    // .range loop, never .limit — one client's client-level docs are few today, but every un-paginated
    // load in this codebase has eventually truncated, and >1000 is silently capped with no error.
    const out = [];
    let from = 0;
    for (;;) {
      let q = supabase.from('file_exchange')
        // ⛔ extraction_skipped_at IS SELECTED SO THE MARKER CAN RENDER. A document filed without scanning leaves
        // the same trace as one whose extraction failed unnoticed — no values anywhere — so the reader needs to be
        // told which, here, where they meet the document.
        .select('id, file_name, file_type, type_key, folder_name, folder_key, year_subfolder, storage_path, filed_at, filed_by, extraction_skipped_at');
      q = forConsultant ? q.eq('consultant_id', consultantId) : q.eq('client_id', clientId).is('property_id', null);
      const { data, error } = await q
        .not('filed_at', 'is', null)                // filed documents only — not the intake tray
        .not('file_name', 'like', '[REMOVED%')      // soft-deleted (same marker DocumentsBrowser excludes)
        .order('filed_at', { ascending: false })
        .range(from, from + 999);
      // Surface the failure. An empty list rendered over a failed read is indistinguishable from
      // "no documents" — that ambiguity is the whole reason this view was needed.
      if (error) { setErr(error.message || String(error)); setLoading(false); return; }
      out.push(...(data || []));
      if (!data || data.length < 1000) break;
      from += 1000;
    }
    setFiles(out);
    setLoading(false);
  }, [clientId, consultantId, forConsultant]);

  useEffect(() => { load(); }, [load]);

  async function openFile(f) {
    if (!f.storage_path) return;
    const url = await fileUrl(f.storage_path, 'open').catch(() => null);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else window.alert('Could not open the file.');
  }

  // Group by whatever folder comes back — no allow-list. Grouped by KEY, shown by the table's current NAME (falling
  // back to the stored name while the table loads), so a renamed folder shows its new name here too.
  const folderTitle = (f) => (f.folder_key && vocab?.folder(f.folder_key)?.name) || f.folder_name || 'Other';
  const folders = {};
  files.forEach(f => { const k = folderTitle(f); (folders[k] = folders[k] || []).push(f); });
  const folderNames = Object.keys(folders).sort();

  function FileRow({ f }) {
    const stamp = [f.filed_by && `filed by ${f.filed_by}`, fmtDate(f.filed_at)].filter(Boolean).join(' · ');
    return (
      <div onClick={() => openFile(f)}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', borderBottom: `1px solid ${c.border}` }}
        onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <span style={{ fontSize: '14px', flexShrink: 0 }}>📄</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.file_name}
            {/* Quiet, not loud: a deliberate skip is a normal state, not a fault. It exists so an empty Proposed
                Values panel is not read as missing data. */}
            {f.extraction_skipped_at && <span title={skipTooltip(skipNotes[f.id])}
              style={{ marginLeft: '8px', fontSize: '10px', color: c.muted, border: `1px solid ${c.border}`, borderRadius: '4px', padding: '1px 5px', whiteSpace: 'nowrap' }}>not scanned</span>}
          </div>
          <div style={{ fontSize: '11px', color: c.muted, marginTop: '2px' }}>
            {(f.type_key && vocab?.type(f.type_key)?.name) || f.file_type || 'Document'}{stamp ? ` · ${stamp}` : ''}
          </div>
        </div>
        <span style={{ ...s.badge(f.year_subfolder ? '96,165,250' : '148,163,184'), flexShrink: 0 }}>
          {f.year_subfolder || 'No year'}
        </span>
      </div>
    );
  }

  return (
    <div style={{ ...s.card, marginBottom: '16px' }}>
      <div style={s.cardHeader}>
        <div style={s.cardTitle}>{forConsultant ? '📂 Documents' : '📁 Client Documents'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '11px', color: c.muted }}>{forConsultant
            ? `Filed against ${consultantName || 'this consultant'} · staff only`
            : `Filed to ${clientName || 'this client'} · no property`}</span>
          <span style={s.badge('96,165,250')}>{files.length} file{files.length !== 1 ? 's' : ''}</span>
          <button onClick={load} disabled={loading}
            style={{ ...s.btnSm, background: 'transparent', color: loading ? c.muted : 'var(--red)', border: `1px solid ${loading ? c.border : 'rgba(180,0,0,0.3)'}` }}>
            {loading ? '⏳' : '↻ Refresh'}
          </button>
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {err ? (
          <div style={{ padding: '12px 14px', borderRadius: '7px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', fontSize: '12px', color: '#f87171' }}>
            ⚠ Couldn't load {forConsultant ? 'consultant' : 'client'} documents: {err}
            <div style={{ color: c.muted, marginTop: '4px' }}>This is a failed read, not an empty folder — retry with ↻ Refresh.</div>
          </div>
        ) : loading ? (
          <div style={{ padding: '18px', textAlign: 'center', fontSize: '12px', color: c.muted }}>⏳ Loading…</div>
        ) : files.length === 0 ? (
          <div style={{ padding: '18px', textAlign: 'center', fontSize: '13px', color: c.muted }}>
            {forConsultant
              ? 'No documents filed against this consultant yet. Their contracts and reports file here, from Document Intake.'
              : 'No client-level documents yet. Contracts, onboarding checklists and the like file here — property documents live on the property.'}
          </div>
        ) : (
          folderNames.map(name => {
            const isOpen = openFolder === name;
            const inFolder = [...folders[name]].sort(byYear);
            return (
              <div key={name}>
                <div onClick={() => setOpenFolder(isOpen ? null : name)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', cursor: 'pointer', borderRadius: '7px', background: isOpen ? c.activeNav : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px' }}>📁</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: c.text }}>{name}</span>
                    <span style={s.badge('96,165,250')}>{inFolder.length}</span>
                  </div>
                  <span style={{ fontSize: '10px', color: c.muted }}>{isOpen ? '▲' : '▶'}</span>
                </div>
                {isOpen && <div style={{ marginLeft: '14px', marginBottom: '6px' }}>{inFolder.map(f => <FileRow key={f.id} f={f} />)}</div>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
